// displayRules.ts — map colours & states as data (ST-00276 phase 2, 2026-10-20)
//
// ONE source, two byte-identical copies (the client's Docker build context is client/ and the
// server's image copies only server/, so neither can import the other or a workspace package):
//   server/modules/lexistar/display/displayRules.ts   the source (the server evaluates the live state)
//   client/src/components/map/displayRules.ts          a copy (Replay, the settings page's preview)
// client/src/components/map/displayRules.copy.test.ts fails when the two differ: edit the server
// file, then `cp` it over the client one. Pure TypeScript with no imports: no React, no DOM, no
// Node, no database.
//
// WHAT IT IS. An organisation's display rules are an ordered list of "when <condition> show
// <visual>" (phase 1's VisualRule shape, client/src/components/map/assetVisualRules.ts, extended):
//   * layer 'base'    — the FIRST matching base rule gives the truck's colour (a palette token,
//                       the phase 1 status keys, each with light and dark values) and its words;
//   * layer 'overlay' — EVERY matching overlay rule adds a mark (badge / ring / pulse / dashed),
//                       highest priority first, because one colour cannot carry several states.
// The marker's shape and icon always back the colour (colour-blind safe); this module only
// says which token and which marks.
//
// CONDITIONS are plain data: the notification rules' leaf { path, op, value } (the same
// operators), an active-event leaf { event, within_s } ("fuel loss suspected in the last 2 h"),
// combined with all / any / not / always. Phase 1's leaves ({ field, is }, { field, in },
// { flag }) and overlay visuals ({ shape }, { badge }, { ring }) are still read (normaliseRule).
// `path` is a field of the FIELD CATALOGUE below; a device reading the catalogue does not list
// yet is `reading.<engine document key>` (the catalogue grows as fields appear).
//
// FACTS. A truck's facts are a flat map field id -> value (factsOfBoardRow for a status board
// row, factsOfReplayMoment for a replayed moment, factsOfMotion for a tracker LexiStar does not
// know) plus its recent events (type -> newest event time, ms). Adding a derived metric is ONE
// registration in FIELD_CATALOGUE: an entry with `derive(facts)` and its `inputs`.
//
// DEFAULTS. DEFAULT_DISPLAY_RULES are phase 1's DEFAULT_RULES written in the new leaves (today's
// palette): an organisation without saved rules gets them (rules_version 0). The golden test
// (client/src/components/map/displayRules.golden.test.ts) asserts they give phase 1's resolver's
// token, label and marks over a matrix of states.
//
// COST. compileRules() turns a rule set into closures once per (organisation, version);
// evaluateDisplay() is then a few closure calls per truck (2,000 trucks: well under 10 ms).

// ── Palette tokens and marks ──────────────────────────────────────────────────────────────────

/** The palette tokens (phase 1's status keys), in legend order. */
export const DISPLAY_TOKENS = [
  'to_source',
  'at_source',
  'loaded',
  'at_destination',
  'empty',
  'delay',
  'parked',
  'maintenance',
  'planned_maintenance',
  'breakdown',
  'offline',
  'unallocated',
] as const;
export type DisplayToken = (typeof DISPLAY_TOKENS)[number];

/** A token's words (phase 1's ASSET_STATUS label and hint). */
export const TOKEN_TEXT: Record<DisplayToken, { label: string; hint: string }> = {
  to_source: { label: 'To source', hint: 'Empty, on the way to load' },
  at_source: { label: 'At source', hint: 'Loading' },
  loaded: { label: 'Travelling full', hint: 'Loaded, on the way to offload' },
  at_destination: { label: 'At destination', hint: 'Offloading' },
  empty: { label: 'Travelling empty', hint: 'Offloaded, on the way back' },
  delay: { label: 'Delayed', hint: 'Held up: a delay zone, stopped en route, a queue, no driver' },
  parked: { label: 'Parked', hint: 'Standing, no trip under way' },
  maintenance: { label: 'In maintenance', hint: 'Under maintenance or out of service' },
  planned_maintenance: {
    label: 'Planned maintenance',
    hint: 'Booked into the workshop: a service, tyres, an inspection',
  },
  breakdown: { label: 'Breakdown', hint: 'Broken down: not available until it is repaired' },
  offline: { label: 'Offline / no data', hint: 'No recent position' },
  unallocated: { label: 'Unallocated', hint: 'Moving without a trip' },
};

/** Tokens drawn hollow (dashed) on the marker. */
export const HOLLOW_TOKENS: readonly DisplayToken[] = ['offline'];

export const isDisplayToken = (v: unknown): v is DisplayToken =>
  typeof v === 'string' && (DISPLAY_TOKENS as readonly string[]).includes(v);

/** The overlay marks. */
export const OVERLAY_MARKS = ['badge', 'ring', 'pulse', 'dashed'] as const;
export type OverlayMark = (typeof OVERLAY_MARKS)[number];

export const MARK_TEXT: Record<OverlayMark, { label: string; hint: string }> = {
  badge: { label: 'Badge', hint: 'A small tag with your words next to the truck' },
  ring: { label: 'Ring', hint: 'A ring in a colour around the truck' },
  pulse: {
    label: 'Pulsing ring',
    hint: 'A ring that pulses to call attention (steady when motion is reduced)',
  },
  dashed: { label: 'Dashed outline', hint: 'The truck is drawn hollow with a dashed outline' },
};

export const isOverlayMark = (v: unknown): v is OverlayMark =>
  typeof v === 'string' && (OVERLAY_MARKS as readonly string[]).includes(v);

// ── Facts ─────────────────────────────────────────────────────────────────────────────────────

export type Scalar = string | number | boolean | null;
export type FactValue = Scalar | readonly Scalar[] | undefined;
/** A truck's facts: field id -> value (a set field holds an array). */
export type Facts = Record<string, FactValue>;
/** A truck's recent events: event type -> the newest event time (ms). */
export type RecentEvents = Readonly<Record<string, number>>;

// ── Conditions ────────────────────────────────────────────────────────────────────────────────

/** The notification rules' operators (ruleLogic.ts CONDITION_OPS), plus not_contains for sets. */
export const CONDITION_OPS = [
  'eq',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'not_in',
  'contains',
  'not_contains',
  'starts_with',
  'exists',
  'not_exists',
] as const;
export type ConditionOp = (typeof CONDITION_OPS)[number];
export const NO_VALUE_OPS: readonly ConditionOp[] = ['exists', 'not_exists'];
export const LIST_OPS: readonly ConditionOp[] = ['in', 'not_in'];

export const OP_TEXT: Record<ConditionOp, string> = {
  eq: 'is',
  ne: 'is not',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  in: 'is one of',
  not_in: 'is none of',
  contains: 'includes',
  not_contains: 'does not include',
  starts_with: 'starts with',
  exists: 'is present',
  not_exists: 'is missing',
};

/** A field test, in the notification rules' shape. */
export interface FieldCondition {
  path: string;
  op: ConditionOp;
  value?: Scalar | Scalar[];
}
/** An event of this type (or CATEGORY.*) in the last `within_s` seconds. */
export interface EventCondition {
  event: string;
  within_s: number;
}
/** Phase 1's leaves (read, written back as FieldCondition). */
export type LegacyLeaf =
  { field: string; is: Scalar } | { field: string; in: Scalar[] } | { flag: string };

export type Condition =
  | { always: true }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition }
  | FieldCondition
  | EventCondition
  | LegacyLeaf;

// ── Rules ─────────────────────────────────────────────────────────────────────────────────────

export type DisplayLayer = 'base' | 'overlay';

/** What a rule shows. Base: `key` (the token) and optional words. Overlay: `mark`, `key`, `label`. */
export interface DisplayVisual {
  /** Base: the colour token. Overlay: the ring / pulse / badge colour (optional for a badge). */
  key?: DisplayToken;
  /** Base: the words (default: the token's label). Badge: its words. */
  label?: string;
  /** Overlay: the mark. */
  mark?: OverlayMark;
  // Phase 1's overlay encoding (read only):
  shape?: 'dashed';
  badge?: string;
  ring?: DisplayToken;
}

export interface DisplayRule {
  /** Stable id: [a-z0-9] then [a-z0-9_-], at most 64. */
  id: string;
  /** What people call it (the settings page; the legend of a custom rule). */
  name?: string;
  layer: DisplayLayer;
  when: Condition;
  /** Overlays: higher first (ties keep rule order). */
  priority?: number;
  visual: DisplayVisual;
  /** The legend's words for this rule. */
  legend?: string;
  /** false: kept in the list, not applied. */
  enabled?: boolean;
}

// ── Output ────────────────────────────────────────────────────────────────────────────────────

export interface DisplayBase {
  token: DisplayToken;
  label: string;
  rule_id: string;
}
export interface DisplayOverlay {
  mark: OverlayMark;
  token: DisplayToken | null;
  /** The mark's words: a badge's text, else the rule's name or legend (null: none given). */
  label: string | null;
  rule_id: string;
}
/** What every truck carries: one colour, its marks, and the rule set that produced them. */
export interface AssetDisplay {
  base: DisplayBase;
  overlays: DisplayOverlay[];
  rules_version: number;
}

/** Draw the marker hollow and dashed: a hollow token (offline) or a dashed overlay. */
export const isHollowDisplay = (d: Pick<AssetDisplay, 'base' | 'overlays'>): boolean =>
  HOLLOW_TOKENS.includes(d.base.token) || d.overlays.some((o) => o.mark === 'dashed');

// ── The field catalogue ───────────────────────────────────────────────────────────────────────

export type FieldKind = 'enum' | 'boolean' | 'number' | 'text' | 'set';
export type FieldGroup = 'state' | 'trip' | 'zone' | 'maintenance' | 'fuel' | 'derived' | 'reading';
export type FieldSource =
  'status' | 'trip' | 'zones' | 'maintenance' | 'fuel' | 'derived' | 'device' | 'can';

export interface FieldOption {
  value: string;
  label: string;
}

/** A status board row as the catalogue reads it (server/types/lexistar-status-board shapes). */
export interface BoardRowLike {
  status?: {
    context?: string | null;
    motion?: string | null;
    overlays?: readonly string[] | null;
    override?: { mode?: string | null } | null;
    time_in_status_s?: number | null;
  } | null;
  position?: { ts?: string | null; speed_kmh?: number | null } | null;
  last_update?: string | null;
  trip?: {
    stage?: string | null;
    ack_reminders?: number | null;
    awaiting_first_report?: boolean | null;
    pct_distance?: number | null;
    km_remaining?: number | null;
    eta_delta_min?: number | null;
    open_deviations?: number | null;
    above_route_norm?: boolean | null;
  } | null;
  maintenance?: { kind?: string | null; overdue?: boolean | null } | null;
  delay?: unknown;
  zones?: ReadonlyArray<{ name?: string | null; purpose_type?: string | null }> | null;
  dwell_breach?: boolean | null;
  fuel?: {
    pct_budget?: number | null;
    burn_index?: number | null;
    burn_band?: string | null;
    over_under_l?: number | null;
    budget_adjusted_l?: number | null;
    consumed_l?: number | null;
  } | null;
  driver_name?: string | null;
  driver_tag?: string | null;
}

/** What a catalogue reader gets for one truck. */
export interface FactSource {
  row: BoardRowLike;
  /** The device readings: the engine's last-known document, else the latest position row. */
  readings: Readonly<Record<string, unknown>> | null;
  nowMs: number;
  /** Phase 1's state of the row (computed once per truck). */
  state: RowState;
}

export interface FieldDef {
  id: string;
  label: string;
  group: FieldGroup;
  kind: FieldKind;
  unit?: string;
  options?: FieldOption[];
  source: FieldSource;
  description: string;
  /** 'always': every organisation with LexiStar trucks has it; 'reported': only once its devices report `reading`. */
  availability: 'always' | 'reported';
  /** The engine last-known document key (= telemetry.positions column) a reading comes from. */
  reading?: string;
  /** Replay has it at every replayed moment (else the legend says it cannot be shown historically). */
  replay: boolean;
  /** Derived metrics: the fields it is computed from (available when they all are). */
  inputs?: string[];
  read?: (src: FactSource) => FactValue;
  derive?: (facts: Facts) => FactValue;
}

const opts = (pairs: Array<[string, string]>): FieldOption[] =>
  pairs.map(([value, label]) => ({ value, label }));

export const CONTEXT_OPTIONS = opts([
  ['EN_ROUTE_TO_SOURCE', 'En route to source'],
  ['AT_SOURCE', 'At source'],
  ['IN_TRANSIT_LOADED', 'In transit (loaded)'],
  ['AT_DESTINATION', 'At destination'],
  ['IN_TRANSIT_EMPTY', 'In transit (empty)'],
  ['AT_WEIGHBRIDGE', 'At weighbridge'],
  ['IN_DELAY_ZONE', 'In a delay zone'],
  ['AT_DEPOT', 'At depot'],
  ['STANDBY', 'Standby'],
  ['UNDER_MAINTENANCE', 'Under maintenance'],
  ['OUT_OF_SERVICE', 'Out of service'],
  ['UNALLOCATED', 'Unallocated'],
]);
export const STAGE_OPTIONS = opts([
  ['DISPATCHED', 'Dispatched'],
  ['ACKNOWLEDGED', 'Acknowledged'],
  ['EN_ROUTE_TO_SOURCE', 'En route to source'],
  ['AT_SOURCE', 'At source'],
  ['IN_TRANSIT_LOADED', 'In transit (loaded)'],
  ['AT_DESTINATION', 'At destination'],
  ['IN_TRANSIT_EMPTY', 'In transit (empty)'],
  ['EXCEPTION_HOLD', 'On hold'],
  ['COMPLETED', 'Completed'],
]);
const MOTION_OPTIONS = opts([
  ['moving', 'Moving'],
  ['idling', 'Idling'],
  ['stopped', 'Stopped'],
  ['parked', 'Parked'],
]);
const MAINTENANCE_OPTIONS = opts([
  ['breakdown', 'Breakdown'],
  ['planned', 'Planned maintenance'],
  ['under_maintenance', 'Under maintenance'],
  ['out_of_service', 'Out of service'],
]);
const FLAG_OPTIONS = opts([
  ['driver_reminded', 'Driver reminded to accept'],
  ['awaiting_first_report', 'Waiting for the first report'],
  ['alert_open', 'An alert is open'],
  ['deviating', 'Off the approved route'],
  ['no_driver', 'No driver'],
]);
const PURPOSE_OPTIONS = opts([
  ['site', 'Site'],
  ['source', 'Source'],
  ['destination', 'Destination'],
  ['weighbridge', 'Weighbridge'],
  ['delay', 'Delay'],
  ['maintenance', 'Maintenance / workshop'],
  ['no_go', 'No-go'],
  ['depot', 'Depot / yard'],
  ['fuel_station', 'Fuel station'],
  ['rest_stop', 'Rest / safe stop'],
  ['speed_zone', 'Speed zone'],
  ['tipping_zone', 'Tipping zone'],
  ['risk_zone', 'Risk zone'],
  ['border_post', 'Border post'],
  ['time_and_motion', 'Time and motion'],
  ['custom', 'Custom'],
]);
const BURN_BAND_OPTIONS = opts([
  ['normal', 'Normal'],
  ['warning', 'Warning'],
  ['critical', 'Critical'],
]);

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const pct = (v: unknown): number | null => {
  const n = numOrNull(v);
  return n === null ? null : Math.round(n * 1000) / 10;
};
const reading =
  (key: string) =>
  (src: FactSource): FactValue => {
    const v = src.readings ? src.readings[key] : undefined;
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string') return v;
    return null;
  };

/**
 * Every field a condition can test. `id` is what rules store; never rename one. To add a field,
 * add one entry: `read` (from the status board row and its readings), or `derive` + `inputs` for a
 * metric computed from other facts. A device reading needs only `reading` (its engine document key).
 */
export const FIELD_CATALOGUE: FieldDef[] = [
  // Live state (phase 1's AssetState fields keep their ids).
  {
    id: 'context',
    label: 'Operational status',
    group: 'state',
    kind: 'enum',
    options: CONTEXT_OPTIONS,
    source: 'status',
    description:
      'Where the truck is in its work: the manual override wins; the trip stage stands in until the engine has one',
    availability: 'always',
    replay: true,
    read: (s) => s.state.context,
  },
  {
    id: 'motion',
    label: 'Motion',
    group: 'state',
    kind: 'enum',
    options: MOTION_OPTIONS,
    source: 'status',
    description: "The engine's motion (moving, idling, stopped, parked); else the last speed",
    availability: 'always',
    replay: false,
    read: (s) => s.state.motion,
  },
  {
    id: 'offline',
    label: 'Offline',
    group: 'state',
    kind: 'boolean',
    source: 'status',
    description: 'No data for 90 minutes, no data at all, or the engine says offline',
    availability: 'always',
    replay: false,
    read: (s) => s.state.offline,
  },
  {
    id: 'stale',
    label: 'No recent position',
    group: 'state',
    kind: 'boolean',
    source: 'status',
    description: 'The position is too old to trust where the truck is',
    availability: 'always',
    replay: false,
    read: (s) => s.state.stale,
  },
  {
    id: 'known',
    label: 'In the LexiStar register',
    group: 'state',
    kind: 'boolean',
    source: 'status',
    description:
      'false for a tracker LexiStar has no status for (Live View then knows only its motion)',
    availability: 'always',
    replay: true,
    read: () => true,
  },
  {
    id: 'flags',
    label: 'Flags',
    group: 'state',
    kind: 'set',
    options: FLAG_OPTIONS,
    source: 'status',
    description: 'Alert open, off route, no driver, driver reminded, waiting for the first report',
    availability: 'always',
    replay: false,
    read: (s) => s.state.flags,
  },
  {
    id: 'override',
    label: 'Manual override in force',
    group: 'state',
    kind: 'boolean',
    source: 'status',
    description: 'Someone set the truck’s status by hand',
    availability: 'always',
    replay: false,
    read: (s) => !!s.row.status?.override?.mode,
  },
  {
    id: 'time_in_status_s',
    label: 'Time in this status',
    group: 'state',
    kind: 'number',
    unit: 's',
    source: 'status',
    description: 'Seconds since the status last changed',
    availability: 'always',
    replay: false,
    read: (s) => numOrNull(s.row.status?.time_in_status_s),
  },
  {
    id: 'data_age_s',
    label: 'Data age',
    group: 'state',
    kind: 'number',
    unit: 's',
    source: 'status',
    description: 'Seconds since the newest position or report',
    availability: 'always',
    replay: false,
    read: (s) => s.state.ageS,
  },
  // Trip.
  {
    id: 'stage',
    label: 'Trip stage',
    group: 'trip',
    kind: 'enum',
    options: STAGE_OPTIONS,
    source: 'trip',
    description: 'The stage of the trip the truck is working (missing: no trip)',
    availability: 'always',
    replay: true,
    read: (s) => s.state.stage,
  },
  {
    id: 'trip.progress_pct',
    label: 'Trip progress',
    group: 'trip',
    kind: 'number',
    unit: '%',
    source: 'trip',
    description: 'Distance travelled as a share of the planned distance',
    availability: 'always',
    replay: false,
    read: (s) => pct(s.row.trip?.pct_distance),
  },
  {
    id: 'trip.km_remaining',
    label: 'Distance remaining',
    group: 'trip',
    kind: 'number',
    unit: 'km',
    source: 'trip',
    description: 'Planned distance not travelled yet',
    availability: 'always',
    replay: false,
    read: (s) => numOrNull(s.row.trip?.km_remaining),
  },
  {
    id: 'trip.eta_delta_min',
    label: 'ETA against plan',
    group: 'trip',
    kind: 'number',
    unit: 'min',
    source: 'trip',
    description: 'Minutes the ETA is after the planned end (negative: early)',
    availability: 'always',
    replay: false,
    read: (s) => numOrNull(s.row.trip?.eta_delta_min),
  },
  {
    id: 'trip.open_deviations',
    label: 'Open route deviations',
    group: 'trip',
    kind: 'number',
    source: 'trip',
    description: 'Route deviations of the trip that are still open',
    availability: 'always',
    replay: false,
    read: (s) => numOrNull(s.row.trip?.open_deviations),
  },
  {
    id: 'trip.above_route_norm',
    label: 'Distance above the route norm',
    group: 'trip',
    kind: 'boolean',
    source: 'trip',
    description: 'The trip is flagged above its route’s historical distance',
    availability: 'always',
    replay: false,
    read: (s) => (s.row.trip ? !!s.row.trip.above_route_norm : null),
  },
  // Zones.
  {
    id: 'zone.purposes',
    label: 'In a zone of type',
    group: 'zone',
    kind: 'set',
    options: PURPOSE_OPTIONS,
    source: 'zones',
    description: 'The purposes of the geofences the truck is in right now',
    availability: 'always',
    replay: false,
    read: (s) => (s.row.zones || []).map((z) => z.purpose_type || 'custom'),
  },
  {
    id: 'zone.names',
    label: 'In the zone named',
    group: 'zone',
    kind: 'set',
    source: 'zones',
    description: 'The names of the geofences the truck is in right now',
    availability: 'always',
    replay: false,
    read: (s) => (s.row.zones || []).map((z) => z.name).filter((n): n is string => !!n),
  },
  {
    id: 'dwell_breach',
    label: 'Over the dwell limit',
    group: 'zone',
    kind: 'boolean',
    source: 'zones',
    description: 'In a zone longer than its dwell rule allows',
    availability: 'always',
    replay: false,
    read: (s) => !!s.row.dwell_breach,
  },
  // Maintenance and delay.
  {
    id: 'maintenance',
    label: 'Maintenance',
    group: 'maintenance',
    kind: 'enum',
    options: MAINTENANCE_OPTIONS,
    source: 'maintenance',
    description:
      'Breakdown, planned maintenance, under maintenance or out of service (missing: none)',
    availability: 'always',
    replay: false,
    read: (s) => s.state.maintenance,
  },
  {
    id: 'maintenance.overdue',
    label: 'Maintenance overdue',
    group: 'maintenance',
    kind: 'boolean',
    source: 'maintenance',
    description: 'The maintenance or breakdown is open past its expected end',
    availability: 'always',
    replay: false,
    read: (s) => (s.row.maintenance ? !!s.row.maintenance.overdue : null),
  },
  {
    id: 'delayed',
    label: 'Delay declared',
    group: 'maintenance',
    kind: 'boolean',
    source: 'maintenance',
    description: 'An open delay ("Queue at loader", "Driver unavailable" …)',
    availability: 'always',
    replay: false,
    read: (s) => s.state.delayed,
  },
  // Fuel (the trip's live fuel figures, LS-04 §9).
  {
    id: 'fuel.pct_budget',
    label: 'Fuel used of budget',
    group: 'fuel',
    kind: 'number',
    unit: '%',
    source: 'fuel',
    description: 'Litres consumed as a share of the trip’s fuel budget',
    availability: 'always',
    replay: false,
    read: (s) => pct(s.row.fuel?.pct_budget),
  },
  {
    id: 'fuel.burn_index',
    label: 'Burn index',
    group: 'fuel',
    kind: 'number',
    source: 'fuel',
    description:
      'Share of budget used ÷ share of distance done (1 = on the norm; shown from 10 % of the trip)',
    availability: 'always',
    replay: false,
    read: (s) => numOrNull(s.row.fuel?.burn_index),
  },
  {
    id: 'fuel.burn_band',
    label: 'Burn band',
    group: 'fuel',
    kind: 'enum',
    options: BURN_BAND_OPTIONS,
    source: 'fuel',
    description: 'The burn index as normal, warning or critical',
    availability: 'always',
    replay: false,
    read: (s) => s.row.fuel?.burn_band ?? null,
  },
  {
    id: 'fuel.over_under_l',
    label: 'Projected under (+) / over (−) budget',
    group: 'fuel',
    kind: 'number',
    unit: 'L',
    source: 'fuel',
    description: 'Budget minus the projected total for the trip (negative: over budget)',
    availability: 'always',
    replay: false,
    read: (s) => numOrNull(s.row.fuel?.over_under_l),
  },
  {
    id: 'fuel.budget_l',
    label: 'Fuel budget',
    group: 'fuel',
    kind: 'number',
    unit: 'L',
    source: 'fuel',
    description: 'The trip’s fuel budget (adjusted)',
    availability: 'always',
    replay: false,
    read: (s) => numOrNull(s.row.fuel?.budget_adjusted_l),
  },
  // Derived metrics: one registration each (`derive` over other facts).
  {
    id: 'derived.projected_overrun_pct',
    label: 'Projected fuel overrun',
    group: 'derived',
    kind: 'number',
    unit: '%',
    source: 'derived',
    description:
      'How far over its fuel budget the trip is projected to end, as a share of the budget (0 when under)',
    availability: 'always',
    replay: false,
    inputs: ['fuel.over_under_l', 'fuel.budget_l'],
    derive: (f) => {
      const ou = numOrNull(f['fuel.over_under_l']);
      const budget = numOrNull(f['fuel.budget_l']);
      if (ou === null || budget === null || budget <= 0) return null;
      return Math.round((Math.max(0, -ou) / budget) * 1000) / 10;
    },
  },
  // Device readings (the engine's last-known document; available once the devices report them).
  {
    id: 'reading.speed_kmh',
    label: 'Speed',
    group: 'reading',
    kind: 'number',
    unit: 'km/h',
    source: 'device',
    description: 'GPS speed of the newest position',
    availability: 'reported',
    reading: 'speed_kmh',
    replay: true,
    read: reading('speed_kmh'),
  },
  {
    id: 'reading.ignition',
    label: 'Ignition on',
    group: 'reading',
    kind: 'boolean',
    source: 'device',
    description: 'The ignition input of the tracker',
    availability: 'reported',
    reading: 'ignition',
    replay: true,
    read: reading('ignition'),
  },
  {
    id: 'reading.movement',
    label: 'Movement sensor',
    group: 'reading',
    kind: 'boolean',
    source: 'device',
    description: 'The tracker’s own movement sensor',
    availability: 'reported',
    reading: 'movement',
    replay: false,
    read: reading('movement'),
  },
  {
    id: 'reading.ext_voltage_v',
    label: 'Vehicle battery voltage',
    group: 'reading',
    kind: 'number',
    unit: 'V',
    source: 'device',
    description: 'The external (vehicle) supply voltage at the tracker',
    availability: 'reported',
    reading: 'ext_voltage_v',
    replay: false,
    read: reading('ext_voltage_v'),
  },
  {
    id: 'reading.backup_battery_v',
    label: 'Tracker backup battery',
    group: 'reading',
    kind: 'number',
    unit: 'V',
    source: 'device',
    description: 'The tracker’s internal backup battery voltage',
    availability: 'reported',
    reading: 'backup_battery_v',
    replay: false,
    read: reading('backup_battery_v'),
  },
  {
    id: 'reading.gsm_signal',
    label: 'GSM signal',
    group: 'reading',
    kind: 'number',
    source: 'device',
    description: 'Mobile signal strength, 0 (none) to 5',
    availability: 'reported',
    reading: 'gsm_signal',
    replay: false,
    read: reading('gsm_signal'),
  },
  {
    id: 'reading.sats',
    label: 'Satellites',
    group: 'reading',
    kind: 'number',
    source: 'device',
    description: 'GNSS satellites in use',
    availability: 'reported',
    reading: 'sats',
    replay: false,
    read: reading('sats'),
  },
  {
    id: 'reading.driver_tag',
    label: 'Driver tag read',
    group: 'reading',
    kind: 'text',
    source: 'device',
    description: 'The driver card or iButton the tracker read',
    availability: 'reported',
    reading: 'driver_tag',
    replay: false,
    read: reading('driver_tag'),
  },
  {
    id: 'reading.fuel_level_pct',
    label: 'Fuel level',
    group: 'reading',
    kind: 'number',
    unit: '%',
    source: 'can',
    description: 'Tank level from the CAN bus or OBD',
    availability: 'reported',
    reading: 'can_fuel_level_pct',
    replay: true,
    read: reading('can_fuel_level_pct'),
  },
  {
    id: 'reading.engine_rpm',
    label: 'Engine RPM',
    group: 'reading',
    kind: 'number',
    unit: 'rpm',
    source: 'can',
    description: 'Engine speed from the CAN bus or OBD',
    availability: 'reported',
    reading: 'can_engine_rpm',
    replay: false,
    read: reading('can_engine_rpm'),
  },
  {
    id: 'reading.can_speed_kmh',
    label: 'Wheel speed (CAN)',
    group: 'reading',
    kind: 'number',
    unit: 'km/h',
    source: 'can',
    description: 'Vehicle speed from the CAN bus or OBD',
    availability: 'reported',
    reading: 'can_wheel_speed_kmh',
    replay: false,
    read: reading('can_wheel_speed_kmh'),
  },
  {
    id: 'reading.odometer_km',
    label: 'Odometer (CAN)',
    group: 'reading',
    kind: 'number',
    unit: 'km',
    source: 'can',
    description: 'Total vehicle distance from the CAN bus',
    availability: 'reported',
    reading: 'can_total_distance_km',
    replay: false,
    read: reading('can_total_distance_km'),
  },
  {
    id: 'reading.engine_hours_h',
    label: 'Engine hours (CAN)',
    group: 'reading',
    kind: 'number',
    unit: 'h',
    source: 'can',
    description: 'Total engine hours from the CAN bus',
    availability: 'reported',
    reading: 'can_engine_hours_h',
    replay: false,
    read: reading('can_engine_hours_h'),
  },
];

export const FIELD_BY_ID: ReadonlyMap<string, FieldDef> = new Map(
  FIELD_CATALOGUE.map((f) => [f.id, f])
);

/** `reading.<key>`: a device reading the catalogue does not list yet (the engine document key). */
export const DISCOVERED_READING = /^reading\.([a-z][a-z0-9_]{0,63})$/;

/**
 * Engine document / position row keys that identify, place or time a record (or carry the status
 * blocks): never a reading a rule may test or the preview may show (an IMEI, an asset id, a
 * message id … would otherwise leak through the colours).
 */
export const NOT_READING_KEYS: ReadonlySet<string> = new Set([
  'v',
  'tenant_id',
  'asset_key',
  'asset_id',
  'device_id',
  'device_external_id',
  'external_id',
  'imei',
  'registration',
  'registration_display',
  'registration_normalised',
  'vehicle_class',
  'msg_id',
  'seq',
  'ts_device',
  'ts_received',
  'provisional',
  'lat',
  'lon',
  'alt_m',
  'heading',
  'source_kind',
  'backfilled',
  'protocol',
  'raw_ref',
  'trigger',
  'extras',
  'clock_skew_s',
  'status',
  'geofences',
]);

/** The raw document keys of the catalogue's own readings (rules name them by their catalogue id). */
const REGISTERED_READING_KEYS: ReadonlySet<string> = new Set(
  FIELD_CATALOGUE.map((f) => f.reading).filter((k): k is string => !!k)
);

/**
 * The document key of a discovered reading id (`reading.<key>`), or null: not that form, an
 * identity / position / time key (NOT_READING_KEYS), or the raw key of a catalogue reading.
 */
export function discoveredReadingKey(id: string): string | null {
  const m = DISCOVERED_READING.exec(id);
  if (!m) return null;
  const key = m[1] as string;
  return NOT_READING_KEYS.has(key) || REGISTERED_READING_KEYS.has(key) ? null : key;
}

const scalarOrNull = (v: unknown): Scalar =>
  typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string' ? v : null;

/** The active events a condition can name (type -> words), by category. */
export const EVENT_CATALOGUE: FieldOption[] = opts([
  ['FUEL.LOSS_SUSPECTED', 'Fuel loss suspected'],
  ['FUEL.BURN_INDEX_HIGH', 'Burn index high'],
  ['FUEL.OVERCONSUMPTION', 'Over-consumption'],
  ['FUEL.OVERCONSUMPTION_PROJECTED', 'Over-consumption projected'],
  ['FUEL.ALLOWANCE_REACHED', 'Fuel allowance reached'],
  ['FUEL.REFUEL_DETECTED', 'Refuel detected'],
  ['FUEL.REFUEL_UNAUTHORISED_LOCATION', 'Refuel at an unauthorised place'],
  ['GEOFENCE.ENTERED', 'Entered a zone'],
  ['GEOFENCE.EXITED', 'Left a zone'],
  ['GEOFENCE.NOGO_ENTERED', 'Entered a no-go zone'],
  ['GEOFENCE.DWELL_EXCEEDED', 'Stayed too long in a zone'],
  ['GEOFENCE.AFTER_HOURS', 'In a zone after hours'],
  ['MOTION.OVERSPEED', 'Overspeeding'],
  ['MOTION.EXCESSIVE_IDLE', 'Idling too long'],
  ['DEVICE.NO_SIGNAL', 'Tracker lost signal'],
  ['DEVICE.SIGNAL_RESTORED', 'Tracker signal restored'],
  ['TRIP.ETA_RISK', 'ETA at risk'],
  ['TRIP.DELAY_AT_SITE', 'Delayed at a site'],
  ['TRIP.NOT_ACKNOWLEDGED', 'Trip not accepted by the driver'],
  ['TRIP.UNALLOCATED_MOVEMENT', 'Moved without a trip'],
  ['TRIP.UNPLANNED_LOAD_DETECTED', 'Unplanned load detected'],
  ['TRIP.DISTANCE_ABOVE_ROUTE_NORM', 'Distance above the route norm'],
  ['ROUTE.DEVIATION_OPENED', 'Left the approved route'],
  ['ROUTE.STOP_WHILE_DEVIATING', 'Stopped while off route'],
  ['ROUTE.DEVIATION_SLA_BREACHED', 'Deviation not handled in time'],
  ['WB.BYPASS', 'Weighbridge bypassed'],
  ['WB.VARIANCE_EXCEEDED', 'Weighbridge variance exceeded'],
  ['COMMS.EMERGENCY_KEYWORD', 'Driver sent an emergency word'],
  ['COMMS.DRIVER_PROBLEM_REPORTED', 'Driver reported a problem'],
]);
export const EVENT_TEXT: ReadonlyMap<string, string> = new Map(
  EVENT_CATALOGUE.map((e) => [e.value, e.label])
);
const EVENT_TYPE = /^[A-Z][A-Z0-9]*\.([A-Z][A-Z0-9_]*|\*)$/;
/** The longest window an event condition may look back (7 days). */
export const MAX_EVENT_WINDOW_S = 7 * 86400;

/** Words for a field id (the catalogue's, else the reading key humanised). */
export function fieldLabel(id: string): string {
  const f = FIELD_BY_ID.get(id);
  if (f) return f.label;
  const m = DISCOVERED_READING.exec(id);
  if (m) {
    const words = (m[1] as string).replace(/^can_/, '').replace(/_/g, ' ');
    return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
  }
  return id;
}

// ── Phase 1's state of a status board row ────────────────────────────────────────────────────

/** The engine's OFFLINE threshold (STATUS_DEFAULTS.offlineAfterS): 90 minutes without data. */
export const OFFLINE_AFTER_S = 90 * 60;

/** A trip stage -> the context the engine derives from it (engine trip.ts tripContextOf). */
export const STAGE_CONTEXT: Readonly<Record<string, string>> = {
  EN_ROUTE_TO_SOURCE: 'EN_ROUTE_TO_SOURCE',
  AT_SOURCE: 'AT_SOURCE',
  IN_TRANSIT_LOADED: 'IN_TRANSIT_LOADED',
  AT_DESTINATION: 'AT_DESTINATION',
  IN_TRANSIT_EMPTY: 'IN_TRANSIT_EMPTY',
};

export type MotionValue = 'moving' | 'idling' | 'stopped' | 'parked';

/** `MOVING` / `moving` -> the rules' motion (TOWED counts as moving). */
export function normaliseMotion(m: string | null | undefined): MotionValue | null {
  switch (String(m ?? '').toUpperCase()) {
    case 'MOVING':
    case 'TOWED':
      return 'moving';
    case 'IDLING':
      return 'idling';
    case 'STOPPED':
      return 'stopped';
    case 'PARKED':
      return 'parked';
    default:
      return null;
  }
}

/** Phase 1's AssetState of a row (StatusBoard/controlRoom.ts assetStateOfRow) plus its data age. */
export interface RowState {
  context: string;
  stage: string | null;
  motion: MotionValue | null;
  offline: boolean;
  stale: boolean;
  maintenance: string | null;
  delayed: boolean;
  flags: string[];
  ageS: number | null;
}

const msOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
};

/** Exactly controlRoom.ts assetStateOfRow (the integration of ST-00244 + ST-00249). */
export function stateOfRow(row: BoardRowLike, nowMs: number): RowState {
  const t = msOf(row.position?.ts || row.last_update || null);
  const ageS = t === null ? null : Math.max(0, Math.floor((nowMs - t) / 1000));
  const ctx = row.status?.override?.mode || row.status?.context || 'UNALLOCATED';
  const stage = row.trip?.stage || null;
  const context =
    (ctx === 'UNALLOCATED' || !row.status?.context) &&
    !row.status?.override?.mode &&
    stage &&
    STAGE_CONTEXT[stage]
      ? (STAGE_CONTEXT[stage] as string)
      : ctx;
  const overlays = Array.isArray(row.status?.overlays)
    ? (row.status?.overlays as readonly string[])
    : [];
  const flags: string[] = [];
  if (row.trip?.stage === 'DISPATCHED' && (Number(row.trip?.ack_reminders) || 0) > 0)
    flags.push('driver_reminded');
  if (row.trip?.awaiting_first_report) flags.push('awaiting_first_report');
  if (overlays.includes('ALERT_OPEN')) flags.push('alert_open');
  if (overlays.includes('DEVIATING')) flags.push('deviating');
  if (overlays.includes('NO_DRIVER')) flags.push('no_driver');
  const offline = ageS === null || overlays.includes('OFFLINE') || ageS >= OFFLINE_AFTER_S;
  const motionRaw = row.status?.motion;
  const speed = row.position?.speed_kmh;
  const moving = motionRaw
    ? motionRaw === 'MOVING' || motionRaw === 'TOWED'
    : speed !== null && speed !== undefined && Number(speed) >= 5;
  return {
    context,
    stage,
    motion: normaliseMotion(motionRaw) ?? (moving ? 'moving' : null),
    offline,
    stale: offline,
    maintenance: row.maintenance?.kind ?? null,
    delayed: !!row.delay,
    flags,
    ageS,
  };
}

// ── Facts builders ────────────────────────────────────────────────────────────────────────────

/**
 * The facts of a status board row (server: every row of GET /status-board). `fields` limits the
 * work to what the rules read — compileRules().fields, which already holds the inputs of the
 * derived metrics the rules name; without it every catalogue field (the settings page's preview).
 */
export function factsOfBoardRow(
  row: BoardRowLike,
  nowMs: number,
  readings: Readonly<Record<string, unknown>> | null = null,
  fields: ReadonlySet<string> | null = null
): Facts {
  const src: FactSource = { row, readings, nowMs, state: stateOfRow(row, nowMs) };
  const facts: Facts = {};
  const wanted = (id: string) => !fields || fields.has(id);
  const derived: FieldDef[] = [];
  for (const f of FIELD_CATALOGUE) {
    if (f.derive) {
      if (wanted(f.id)) derived.push(f);
      continue;
    }
    if (f.read && wanted(f.id)) facts[f.id] = f.read(src);
  }
  for (const f of derived) facts[f.id] = (f.derive as (x: Facts) => FactValue)(facts);
  // Readings the catalogue does not list (reading.<key>): the ones a rule names, or — the
  // preview (no `fields`) — every one the readings carry. Never an identity / position / time key.
  if (readings) {
    if (fields) {
      for (const id of fields) {
        if (facts[id] !== undefined || FIELD_BY_ID.has(id)) continue;
        const key = discoveredReadingKey(id);
        if (key) facts[id] = scalarOrNull(readings[key]);
      }
    } else {
      for (const [key, v] of Object.entries(readings)) {
        const id = `reading.${key}`;
        if (FIELD_BY_ID.has(id) || discoveredReadingKey(id) === null) continue;
        if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string') facts[id] = v;
      }
    }
  }
  return facts;
}

/** A tracker LexiStar does not know, by its motion (Live View's speed + report age; Trips). */
export function factsOfMotion(
  motion: string | null | undefined,
  speedKmh: number | null = null
): Facts {
  return {
    known: false,
    motion:
      motion === 'moving'
        ? 'moving'
        : motion === 'idling'
          ? 'idling'
          : motion === 'parked'
            ? 'parked'
            : null,
    offline: motion === 'offline',
    stale: motion === 'offline',
    'reading.speed_kmh': speedKmh,
  };
}

/** What Replay knows at a replayed moment. */
export interface ReplayMoment {
  stage: string | null | undefined;
  /**
   * Replay's own "moving" (short stops are not the engine's stopped en route). Replay itself
   * leaves it out: the engine's motion is not kept historically (the speed reading is).
   */
  moving?: boolean;
  speed_kmh?: number | null;
  ignition?: boolean | null;
  fuel_pct?: number | null;
}

/** The facts at a replayed moment: the stage (and its context), moving, and the point's readings. */
export function factsOfReplayMoment(m: ReplayMoment): Facts {
  const stage = m.stage ?? null;
  return {
    known: true,
    stage,
    context: (stage && STAGE_CONTEXT[stage]) || null,
    motion: m.moving ? 'moving' : null,
    offline: false,
    stale: false,
    delayed: false,
    'reading.speed_kmh': m.speed_kmh ?? null,
    'reading.ignition': m.ignition ?? null,
    'reading.fuel_level_pct': m.fuel_pct ?? null,
  };
}

/** The fields a rule set reads that Replay does not have (the legend names them). */
export function fieldsNotInReplay(rules: readonly DisplayRule[]): string[] {
  const out = new Set<string>();
  for (const r of rules) {
    if (r.enabled === false) continue;
    for (const id of fieldsOfCondition(normaliseCondition(r.when))) {
      const f = FIELD_BY_ID.get(id);
      if (!f || !f.replay) out.add(id);
    }
  }
  return [...out];
}

// ── Normalising (phase 1's encodings) ────────────────────────────────────────────────────────

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** A condition in the stored form: phase 1's leaves become { path, op, value }. */
export function normaliseCondition(c: Condition): Condition {
  const o = c as unknown as Record<string, unknown>;
  if ('always' in o) return { always: true };
  if ('all' in o) return { all: (o.all as Condition[]).map(normaliseCondition) };
  if ('any' in o) return { any: (o.any as Condition[]).map(normaliseCondition) };
  if ('not' in o) return { not: normaliseCondition(o.not as Condition) };
  if ('flag' in o) return { path: 'flags', op: 'contains', value: o.flag as string };
  if ('field' in o && 'is' in o) {
    const v = o.is as Scalar;
    return v === null
      ? { path: o.field as string, op: 'not_exists' }
      : { path: o.field as string, op: 'eq', value: v };
  }
  if ('field' in o && 'in' in o)
    return { path: o.field as string, op: 'in', value: o.in as Scalar[] };
  // Only the known keys of a leaf are kept (the stored form carries nothing else).
  if ('event' in o) return { event: o.event as string, within_s: o.within_s as number };
  if ('path' in o) {
    const op = o.op as ConditionOp;
    return NO_VALUE_OPS.includes(op) || !('value' in o)
      ? { path: o.path as string, op }
      : { path: o.path as string, op, value: o.value as Scalar | Scalar[] };
  }
  return c;
}

/** A rule in the stored form (normalised condition; overlays as { mark, key, label }). */
export function normaliseRule(r: DisplayRule): DisplayRule {
  const v = r.visual || {};
  let visual: DisplayVisual;
  if (r.layer === 'overlay') {
    if (v.mark)
      visual = {
        mark: v.mark,
        ...(v.key ? { key: v.key } : {}),
        ...(v.label ? { label: v.label } : {}),
      };
    else if (v.shape === 'dashed') visual = { mark: 'dashed' };
    else if (v.ring) visual = { mark: 'ring', key: v.ring, ...(v.label ? { label: v.label } : {}) };
    else if (v.badge) visual = { mark: 'badge', label: v.badge, ...(v.key ? { key: v.key } : {}) };
    else visual = { ...v };
  } else {
    visual = { ...(v.key ? { key: v.key } : {}), ...(v.label ? { label: v.label } : {}) };
  }
  const out: DisplayRule = { id: r.id, layer: r.layer, when: normaliseCondition(r.when), visual };
  if (r.name) out.name = r.name;
  if (r.priority !== undefined) out.priority = r.priority;
  if (r.legend) out.legend = r.legend;
  if (r.enabled === false) out.enabled = false;
  return out;
}

/** JSON with every object's keys sorted (jsonb hands keys back in its own order). */
export const canonicalJson = (v: unknown): string =>
  Array.isArray(v)
    ? `[${v.map(canonicalJson).join(',')}]`
    : v && typeof v === 'object'
      ? `{${Object.keys(v as Record<string, unknown>)
          .sort()
          .map((k) => `${JSON.stringify(k)}:${canonicalJson((v as Record<string, unknown>)[k])}`)
          .join(',')}}`
      : JSON.stringify(v);

/** Two rules say the same (in their stored form). */
export const sameRule = (a: DisplayRule, b: DisplayRule): boolean =>
  canonicalJson(normaliseRule(a)) === canonicalJson(normaliseRule(b));

/** The field ids a (normalised) condition reads. */
export function fieldsOfCondition(c: Condition): string[] {
  const o = c as unknown as Record<string, unknown>;
  if ('all' in o) return (o.all as Condition[]).flatMap(fieldsOfCondition);
  if ('any' in o) return (o.any as Condition[]).flatMap(fieldsOfCondition);
  if ('not' in o) return fieldsOfCondition(o.not as Condition);
  if ('path' in o) return [o.path as string];
  if ('field' in o) return [o.field as string];
  if ('flag' in o) return ['flags'];
  return [];
}

/** The event types (and CATEGORY.* wildcards) a (normalised) condition reads. */
export function eventsOfCondition(c: Condition): Array<{ event: string; within_s: number }> {
  const o = c as unknown as Record<string, unknown>;
  if ('all' in o) return (o.all as Condition[]).flatMap(eventsOfCondition);
  if ('any' in o) return (o.any as Condition[]).flatMap(eventsOfCondition);
  if ('not' in o) return eventsOfCondition(o.not as Condition);
  if ('event' in o) return [{ event: o.event as string, within_s: Number(o.within_s) }];
  return [];
}

// ── Matching ──────────────────────────────────────────────────────────────────────────────────

/** A missing fact reads as phase 1 read it: known true, offline / stale / delayed false. */
const FIELD_DEFAULTS: Readonly<Record<string, Scalar>> = {
  known: true,
  offline: false,
  stale: false,
  delayed: false,
};

const asNumber = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};

/** The notification evaluator's equality (numbers by value, booleans by text); null equals missing. */
function looseEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if ((a === null || a === undefined) && (b === null || b === undefined)) return true;
  const na = asNumber(a);
  const nb = asNumber(b);
  if (na !== null && nb !== null) return na === nb;
  if (typeof a === 'boolean' || typeof b === 'boolean') return String(a) === String(b);
  return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
}

function compare(a: unknown, b: unknown): number | null {
  const na = asNumber(a);
  const nb = asNumber(b);
  if (na !== null && nb !== null) return na - nb;
  if (a === null || a === undefined || b === null || b === undefined) return null;
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
}

type Test = (facts: Facts, events: RecentEvents | null, nowMs: number) => boolean;

const present = (v: FactValue): boolean =>
  Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined;

function leafTest(path: string, op: ConditionOp, expected: Scalar | Scalar[] | undefined): Test {
  const get = (f: Facts): FactValue => {
    const v = f[path];
    return v === undefined && path in FIELD_DEFAULTS ? FIELD_DEFAULTS[path] : v;
  };
  const list: Scalar[] = Array.isArray(expected)
    ? expected
    : expected === undefined
      ? []
      : [expected];
  const one = Array.isArray(expected) ? (expected[0] ?? null) : (expected ?? null);
  const inList = (x: unknown) => list.some((e) => looseEquals(x, e));
  const lower = String(one ?? '').toLowerCase();
  // Each scalar test; a set fact (an array) holds when any element does (none, for the negations).
  const scalar = (x: unknown): boolean => {
    switch (op) {
      case 'eq':
      case 'contains':
        return op === 'contains' && typeof x === 'string'
          ? x.toLowerCase().includes(lower)
          : looseEquals(x, one);
      case 'ne':
      case 'not_contains':
        return !(op === 'not_contains' && typeof x === 'string'
          ? x.toLowerCase().includes(lower)
          : looseEquals(x, one));
      case 'in':
        return inList(x);
      case 'not_in':
        return !inList(x);
      case 'starts_with':
        return typeof x === 'string' && x.startsWith(String(one ?? ''));
      case 'gt':
      case 'gte':
      case 'lt':
      case 'lte': {
        const c = compare(x, one);
        if (c === null) return false;
        return op === 'gt' ? c > 0 : op === 'gte' ? c >= 0 : op === 'lt' ? c < 0 : c <= 0;
      }
      default:
        return false;
    }
  };
  const negation = op === 'ne' || op === 'not_in' || op === 'not_contains';
  if (op === 'exists') return (f) => present(get(f));
  if (op === 'not_exists') return (f) => !present(get(f));
  return (f) => {
    const v = get(f);
    if (Array.isArray(v)) {
      // eq / contains / in on a set: some element; their negations: no element.
      if (op === 'ne' || op === 'not_contains') return !v.some((x) => looseEquals(x, one));
      if (op === 'not_in') return !v.some((x) => inList(x));
      if (op === 'eq' || op === 'contains') return v.some((x) => looseEquals(x, one));
      return v.some((x) => scalar(x));
    }
    if ((v === null || v === undefined) && !negation && op !== 'eq' && op !== 'in') return false;
    return scalar(v);
  };
}

function eventTest(type: string, withinS: number): Test {
  const windowMs = Math.max(0, withinS) * 1000;
  if (type.endsWith('.*')) {
    const prefix = type.slice(0, -1);
    return (_f, events, now) => {
      if (!events) return false;
      for (const k in events)
        if (k.startsWith(prefix) && (events[k] as number) >= now - windowMs) return true;
      return false;
    };
  }
  return (_f, events, now) => {
    const t = events ? events[type] : undefined;
    return t !== undefined && t >= now - windowMs;
  };
}

/** A (normalised or not) condition as one test. */
export function compileCondition(c: Condition): Test {
  const o = normaliseCondition(c) as unknown as Record<string, unknown>;
  if ('always' in o) return () => true;
  if ('all' in o) {
    const parts = (o.all as Condition[]).map(compileCondition);
    return (f, e, n) => parts.every((p) => p(f, e, n));
  }
  if ('any' in o) {
    const parts = (o.any as Condition[]).map(compileCondition);
    return (f, e, n) => parts.some((p) => p(f, e, n));
  }
  if ('not' in o) {
    const inner = compileCondition(o.not as Condition);
    return (f, e, n) => !inner(f, e, n);
  }
  if ('event' in o) return eventTest(String(o.event), Number(o.within_s));
  if ('path' in o)
    return leafTest(String(o.path), o.op as ConditionOp, o.value as Scalar | Scalar[] | undefined);
  return () => false;
}

/** Whether a condition holds for some facts (one-off; rules are compiled once per version). */
export const conditionHolds = (
  c: Condition,
  facts: Facts,
  events: RecentEvents | null = null,
  nowMs: number = Date.now()
): boolean => compileCondition(c)(facts, events, nowMs);

// ── Compiling and evaluating ─────────────────────────────────────────────────────────────────

interface CompiledBase {
  id: string;
  token: DisplayToken;
  label: string;
  test: Test;
}
interface CompiledOverlay {
  id: string;
  mark: OverlayMark;
  token: DisplayToken | null;
  label: string | null;
  test: Test;
}

export interface CompiledRules {
  version: number;
  rules: DisplayRule[];
  base: CompiledBase[];
  overlays: CompiledOverlay[];
  /** The field ids the rules read (factsOfBoardRow's `fields`). */
  fields: ReadonlySet<string>;
  /** Event types / CATEGORY.* the rules read, and the longest window (s). */
  eventTypes: string[];
  maxWithinS: number;
}

/** A rule set as closures, once per (organisation, version). Disabled rules are left out. */
export function compileRules(rules: readonly DisplayRule[], version = 0): CompiledRules {
  const normalised = rules.map(normaliseRule);
  const base: CompiledBase[] = [];
  const overlays: Array<CompiledOverlay & { priority: number; index: number }> = [];
  const fields = new Set<string>();
  const eventTypes = new Set<string>();
  let maxWithinS = 0;
  normalised.forEach((r, index) => {
    if (r.enabled === false) return;
    for (const id of fieldsOfCondition(r.when)) fields.add(id);
    for (const e of eventsOfCondition(r.when)) {
      eventTypes.add(e.event);
      maxWithinS = Math.max(maxWithinS, Number.isFinite(e.within_s) ? e.within_s : 0);
    }
    const test = compileCondition(r.when);
    if (r.layer === 'base') {
      if (!isDisplayToken(r.visual.key)) return;
      base.push({
        id: r.id,
        token: r.visual.key,
        label: r.visual.label || TOKEN_TEXT[r.visual.key].label,
        test,
      });
    } else if (r.layer === 'overlay' && isOverlayMark(r.visual.mark)) {
      overlays.push({
        id: r.id,
        mark: r.visual.mark,
        token: isDisplayToken(r.visual.key) ? r.visual.key : null,
        label: r.visual.label || r.name || r.legend || null,
        test,
        priority: Number(r.priority) || 0,
        index,
      });
    }
  });
  overlays.sort((a, b) => b.priority - a.priority || a.index - b.index);
  // A derived metric reads its inputs.
  for (const id of [...fields])
    for (const input of FIELD_BY_ID.get(id)?.inputs || []) fields.add(input);
  return {
    version,
    rules: normalised,
    base,
    overlays: overlays.map(({ priority: _p, index: _i, ...o }) => o),
    fields,
    eventTypes: [...eventTypes],
    maxWithinS: Math.min(maxWithinS, MAX_EVENT_WINDOW_S),
  };
}

const FALLBACK: DisplayToken = 'parked';

/** One truck's display: the first matching base rule, every matching overlay by priority. */
export function evaluateDisplay(
  compiled: CompiledRules,
  facts: Facts,
  events: RecentEvents | null = null,
  nowMs: number = Date.now()
): AssetDisplay {
  let base: DisplayBase | null = null;
  for (const r of compiled.base) {
    if (r.test(facts, events, nowMs)) {
      base = { token: r.token, label: r.label, rule_id: r.id };
      break;
    }
  }
  const overlays: DisplayOverlay[] = [];
  for (const o of compiled.overlays) {
    if (o.test(facts, events, nowMs))
      overlays.push({ mark: o.mark, token: o.token, label: o.label, rule_id: o.id });
  }
  return {
    base: base || { token: FALLBACK, label: TOKEN_TEXT[FALLBACK].label, rule_id: 'none' },
    overlays,
    rules_version: compiled.version,
  };
}

// ── The default rules (today's palette: phase 1's DEFAULT_RULES) ─────────────────────────────

/** Bumped whenever DEFAULT_DISPLAY_RULES change (a stored "reset to defaults" names it). */
export const DEFAULTS_REVISION = '2026-10-20.1';

const eq = (path: string, value: Scalar): FieldCondition => ({ path, op: 'eq', value });
const oneOf = (path: string, value: Scalar[]): FieldCondition => ({ path, op: 'in', value });
const TRANSIT_CONTEXTS = ['EN_ROUTE_TO_SOURCE', 'IN_TRANSIT_LOADED', 'IN_TRANSIT_EMPTY'];
const BEFORE_LOADED_STAGES = ['DISPATCHED', 'ACKNOWLEDGED', 'EN_ROUTE_TO_SOURCE', 'AT_SOURCE'];
const UNKNOWN = eq('known', false);

export const DEFAULT_DISPLAY_RULES: DisplayRule[] = [
  // A tracker LexiStar does not know: its motion only.
  {
    id: 'untracked-offline',
    name: 'Offline, not in LexiStar',
    layer: 'base',
    when: { all: [UNKNOWN, eq('offline', true)] },
    visual: { key: 'offline', label: 'Offline' },
    legend: 'Offline, not in LexiStar',
  },
  {
    id: 'untracked-moving',
    name: 'Moving, not in LexiStar',
    layer: 'base',
    when: { all: [UNKNOWN, eq('motion', 'moving')] },
    visual: { key: 'unallocated', label: 'Moving' },
    legend: 'Moving, not in LexiStar',
  },
  {
    id: 'untracked',
    name: 'Standing, not in LexiStar',
    layer: 'base',
    when: UNKNOWN,
    visual: { key: 'parked', label: 'Parked' },
    legend: 'Standing, not in LexiStar',
  },
  // Declared states: they hold while the tracker is silent.
  {
    id: 'breakdown',
    name: 'Breakdown',
    layer: 'base',
    when: eq('maintenance', 'breakdown'),
    visual: { key: 'breakdown' },
  },
  {
    id: 'planned-maintenance',
    name: 'Planned maintenance',
    layer: 'base',
    when: eq('maintenance', 'planned'),
    visual: { key: 'planned_maintenance' },
  },
  {
    id: 'maintenance',
    name: 'In maintenance',
    layer: 'base',
    when: {
      any: [
        oneOf('maintenance', ['under_maintenance', 'out_of_service']),
        oneOf('context', ['UNDER_MAINTENANCE', 'OUT_OF_SERVICE']),
      ],
    },
    visual: { key: 'maintenance' },
  },
  {
    id: 'declared-delay',
    name: 'Delay declared',
    layer: 'base',
    when: eq('delayed', true),
    visual: { key: 'delay' },
  },
  {
    id: 'offline',
    name: 'Offline',
    layer: 'base',
    when: eq('offline', true),
    visual: { key: 'offline' },
  },
  // Held up on the way.
  {
    id: 'delay-zone',
    name: 'In a delay zone',
    layer: 'base',
    when: eq('context', 'IN_DELAY_ZONE'),
    visual: { key: 'delay' },
  },
  {
    id: 'trip-on-hold',
    name: 'Trip on hold',
    layer: 'base',
    when: eq('stage', 'EXCEPTION_HOLD'),
    visual: { key: 'delay' },
  },
  {
    id: 'stopped-en-route',
    name: 'Stopped en route',
    layer: 'base',
    when: { all: [oneOf('context', TRANSIT_CONTEXTS), oneOf('motion', ['stopped', 'parked'])] },
    visual: { key: 'delay' },
  },
  // The load cycle (a weighbridge counts on the trip's side; without a trip, the source side).
  {
    id: 'weighbridge-no-trip',
    name: 'At a weighbridge, no trip',
    layer: 'base',
    when: { all: [eq('context', 'AT_WEIGHBRIDGE'), { path: 'stage', op: 'not_exists' }] },
    visual: { key: 'at_source' },
  },
  {
    id: 'weighbridge-source',
    name: 'At a weighbridge, before loading',
    layer: 'base',
    when: { all: [eq('context', 'AT_WEIGHBRIDGE'), oneOf('stage', BEFORE_LOADED_STAGES)] },
    visual: { key: 'at_source' },
  },
  {
    id: 'weighbridge-destination',
    name: 'At a weighbridge, loaded',
    layer: 'base',
    when: eq('context', 'AT_WEIGHBRIDGE'),
    visual: { key: 'at_destination' },
  },
  {
    id: 'to-source',
    name: 'To source',
    layer: 'base',
    when: eq('context', 'EN_ROUTE_TO_SOURCE'),
    visual: { key: 'to_source' },
  },
  {
    id: 'at-source',
    name: 'At source',
    layer: 'base',
    when: eq('context', 'AT_SOURCE'),
    visual: { key: 'at_source' },
  },
  {
    id: 'loaded',
    name: 'Travelling full',
    layer: 'base',
    when: eq('context', 'IN_TRANSIT_LOADED'),
    visual: { key: 'loaded' },
  },
  {
    id: 'at-destination',
    name: 'At destination',
    layer: 'base',
    when: eq('context', 'AT_DESTINATION'),
    visual: { key: 'at_destination' },
  },
  {
    id: 'empty',
    name: 'Travelling empty',
    layer: 'base',
    when: eq('context', 'IN_TRANSIT_EMPTY'),
    visual: { key: 'empty' },
  },
  {
    id: 'dispatched',
    name: 'Dispatched, not moving yet',
    layer: 'base',
    when: oneOf('stage', ['DISPATCHED', 'ACKNOWLEDGED']),
    visual: { key: 'to_source' },
  },
  // Idle.
  {
    id: 'depot',
    name: 'At the depot or on standby',
    layer: 'base',
    when: oneOf('context', ['AT_DEPOT', 'STANDBY']),
    visual: { key: 'parked' },
  },
  {
    id: 'moving-no-trip',
    name: 'Moving without a trip',
    layer: 'base',
    when: eq('motion', 'moving'),
    visual: { key: 'unallocated' },
  },
  {
    id: 'parked',
    name: 'Everything else',
    layer: 'base',
    when: { always: true },
    visual: { key: 'parked' },
  },
  // The overlays that exist today.
  {
    id: 'no-recent-position',
    name: 'No recent position',
    layer: 'overlay',
    priority: 10,
    when: { any: [eq('stale', true), eq('offline', true)] },
    visual: { mark: 'dashed' },
    legend: 'Dashed: no recent position',
  },
  {
    id: 'driver-reminded',
    name: 'Driver reminded',
    layer: 'overlay',
    priority: 5,
    when: {
      all: [{ path: 'flags', op: 'contains', value: 'driver_reminded' }, eq('stage', 'DISPATCHED')],
    },
    visual: { mark: 'badge', label: 'Driver reminded' },
    legend: 'Driver reminded to accept the trip',
  },
];

export const DEFAULT_RULE_IDS: ReadonlySet<string> = new Set(
  DEFAULT_DISPLAY_RULES.map((r) => r.id)
);

let defaultsCompiled: CompiledRules | null = null;
/** The defaults, compiled once (rules_version 0). */
export function compiledDefaults(): CompiledRules {
  if (!defaultsCompiled) defaultsCompiled = compileRules(DEFAULT_DISPLAY_RULES, 0);
  return defaultsCompiled;
}

// ── The legend ────────────────────────────────────────────────────────────────────────────────

export interface LegendEntry {
  /** 'base': a colour the rules give; 'rule': one of your own colour rules; 'overlay': a mark. */
  kind: 'base' | 'rule' | 'overlay';
  id: string;
  label: string;
  hint?: string;
  token: DisplayToken | null;
  mark?: OverlayMark;
}

/**
 * The legend of a rule set: one entry per colour its base rules give (palette order, the
 * palette's words), one per base rule of your own (not a default rule: its name or words), then
 * one per overlay rule. Disabled rules are left out.
 */
export function legendOf(rules: readonly DisplayRule[]): LegendEntry[] {
  const live = rules.map(normaliseRule).filter((r) => r.enabled !== false);
  const used = new Set(
    live.filter((r) => r.layer === 'base' && isDisplayToken(r.visual.key)).map((r) => r.visual.key)
  );
  const colours: LegendEntry[] = DISPLAY_TOKENS.filter((t) => used.has(t)).map((t) => ({
    kind: 'base',
    id: t,
    token: t,
    label: TOKEN_TEXT[t].label,
    hint: TOKEN_TEXT[t].hint,
  }));
  const own: LegendEntry[] = live
    .filter(
      (r) => r.layer === 'base' && isDisplayToken(r.visual.key) && !DEFAULT_RULE_IDS.has(r.id)
    )
    .map((r) => ({
      kind: 'rule',
      id: r.id,
      token: r.visual.key as DisplayToken,
      label: r.legend || r.visual.label || r.name || r.id,
      hint: describeCondition(r.when),
    }));
  const marks: LegendEntry[] = live
    .filter((r) => r.layer === 'overlay' && isOverlayMark(r.visual.mark))
    .sort((a, b) => (Number(b.priority) || 0) - (Number(a.priority) || 0))
    .map((r) => ({
      kind: 'overlay',
      id: r.id,
      token: isDisplayToken(r.visual.key) ? r.visual.key : null,
      mark: r.visual.mark as OverlayMark,
      label: r.legend || r.visual.label || r.name || r.id,
      hint: describeCondition(r.when),
    }));
  return [...colours, ...own, ...marks];
}

// ── Words ─────────────────────────────────────────────────────────────────────────────────────

const valueText = (path: string, v: Scalar | Scalar[] | undefined): string => {
  const options = FIELD_BY_ID.get(path)?.options;
  const one = (x: Scalar) => {
    if (x === null) return 'nothing';
    const o = options?.find((op) => op.value === String(x));
    return o ? o.label : typeof x === 'boolean' ? (x ? 'yes' : 'no') : String(x);
  };
  if (Array.isArray(v)) return v.map(one).join(', ');
  return v === undefined ? '' : one(v);
};

const windowText = (s: number): string =>
  s % 3600 === 0 ? `${s / 3600} h` : s % 60 === 0 ? `${s / 60} min` : `${s} s`;

/** A condition in words: "Operational status is At weighbridge and Trip stage is missing". */
export function describeCondition(c: Condition): string {
  const o = normaliseCondition(c) as unknown as Record<string, unknown>;
  if ('always' in o) return 'always';
  if ('all' in o)
    return (o.all as Condition[]).map((x) => wrap(x, 'any')).join(' and ') || 'always';
  if ('any' in o) return (o.any as Condition[]).map((x) => wrap(x, 'all')).join(' or ') || 'never';
  if ('not' in o) return `not (${describeCondition(o.not as Condition)})`;
  if ('event' in o) {
    const type = String(o.event);
    const name = type.endsWith('.*')
      ? `any ${type.slice(0, -2).toLowerCase()} event`
      : EVENT_TEXT.get(type) || type;
    return `${name} in the last ${windowText(Number(o.within_s))}`;
  }
  if ('path' in o) {
    const path = String(o.path);
    const op = o.op as ConditionOp;
    // A yes / no field reads as "Ignition on: no", "Offline: yes".
    if (
      FIELD_BY_ID.get(path)?.kind === 'boolean' &&
      (op === 'eq' || op === 'ne') &&
      typeof o.value === 'boolean'
    ) {
      return `${fieldLabel(path)}: ${op === 'ne' ? 'not ' : ''}${o.value ? 'yes' : 'no'}`;
    }
    const unit = FIELD_BY_ID.get(path)?.unit;
    const value = NO_VALUE_OPS.includes(op)
      ? ''
      : ` ${valueText(path, o.value as Scalar | Scalar[])}${unit && !LIST_OPS.includes(op) ? ` ${unit}` : ''}`;
    return `${fieldLabel(path)} ${OP_TEXT[op] || op}${value}`;
  }
  return '?';
}

const wrap = (c: Condition, other: 'all' | 'any'): string => {
  const text = describeCondition(c);
  return other in (c as object) ? `(${text})` : text;
};

// ── Validation (the API refuses what fails; the settings page shows the same words inline) ───

export const LIMITS = {
  rules: 100,
  depth: 4,
  leaves: 30,
  listValues: 100,
  label: 40,
  name: 60,
  legend: 120,
  priority: 100,
  /** Characters of one value (a text, a list item). */
  value: 200,
  /**
   * UTF-8 bytes of the whole rule set as the store keeps it: the same measure as the store's
   * CHECK (octet_length(rules::text), at most 256 KB), so multibyte text, jsonb's ", " / ": "
   * spacing or a long-hand number cannot pass the API and then fail the database.
   */
  bytes: 200_000,
} as const;

/** The UTF-8 length of a string (what Postgres' octet_length counts); pure, no TextEncoder needed. */
export function utf8Bytes(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        n += 4; // a surrogate pair: one code point outside the BMP
        i += 1;
      } else n += 3; // a lone surrogate (JSON.stringify escapes these, so it never sees one)
    } else n += 3;
  }
  return n;
}

/** Characters Postgres prints for a number sent as JSON.stringify text (numeric never uses an exponent). */
function numberTextBytes(n: number): number {
  const s = String(n);
  const m = /^(-?)\d(?:\.(\d+))?e([+-])(\d+)$/.exec(s);
  if (!m) return s.length;
  const sign = (m[1] || '').length;
  const frac = (m[2] || '').length;
  const exp = Number(m[4]);
  // 1.5e+21 → 1500000000000000000000; 1.5e-7 → 0.00000015
  return m[3] === '+'
    ? sign + 1 + Math.max(exp, frac) + (frac > exp ? 1 : 0)
    : sign + exp + frac + 2;
}

/**
 * octet_length(value::jsonb::text): the UTF-8 bytes of the value as Postgres prints jsonb, which
 * puts a space after every ":" and ",", and prints numbers long-hand. Key order does not change it.
 */
export function storedJsonBytes(value: unknown): number {
  const walk = (v: unknown): number => {
    if (v === null) return 4;
    if (typeof v === 'boolean') return v ? 4 : 5;
    if (typeof v === 'number') return numberTextBytes(v);
    if (typeof v === 'string') return utf8Bytes(JSON.stringify(v));
    if (Array.isArray(v))
      return v.reduce((n: number, x) => n + walk(x), 2) + Math.max(0, v.length - 1) * 2;
    const entries = Object.entries(v as Record<string, unknown>);
    return (
      entries.reduce((n, [k, x]) => n + utf8Bytes(JSON.stringify(k)) + 2 + walk(x), 2) +
      Math.max(0, entries.length - 1) * 2
    );
  };
  // Through JSON first: what the driver sends (undefined keys dropped, non-finite numbers null).
  const json = JSON.stringify(value);
  return json === undefined ? 0 : walk(JSON.parse(json));
}

/** Whether a condition nests deeper than `max` (iterative: a hostile depth never recurses). */
function nestsDeeper(c: unknown, max: number): boolean {
  const stack: Array<[unknown, number]> = [[c, 1]];
  let seen = 0;
  while (stack.length) {
    const [x, d] = stack.pop() as [unknown, number];
    if (++seen > 10_000) return true;
    if (!isObj(x)) continue;
    if (d > max) return true;
    for (const k of ['all', 'any'] as const) {
      const list = x[k];
      if (Array.isArray(list)) for (const y of list) stack.push([y, d + 1]);
    }
    if ('not' in x) stack.push([x.not, d + 1]);
  }
  return false;
}

const RULE_ID = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const NUMERIC_OPS: readonly ConditionOp[] = ['gt', 'gte', 'lt', 'lte'];
const OPS_BY_KIND: Record<FieldKind, readonly ConditionOp[]> = {
  enum: ['eq', 'ne', 'in', 'not_in', 'exists', 'not_exists'],
  boolean: ['eq', 'ne', 'exists', 'not_exists'],
  number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'exists', 'not_exists'],
  text: [
    'eq',
    'ne',
    'contains',
    'not_contains',
    'starts_with',
    'in',
    'not_in',
    'exists',
    'not_exists',
  ],
  set: ['contains', 'not_contains', 'in', 'not_in', 'exists', 'not_exists'],
};

/** The operators the builder offers for a field. */
export const opsForField = (id: string): readonly ConditionOp[] => {
  const f = FIELD_BY_ID.get(id);
  return f ? OPS_BY_KIND[f.kind] : CONDITION_OPS;
};

export interface ValidationResult {
  ok: boolean;
  errors: string[];
  /** The rules in the stored form (normalised), when ok. */
  rules: DisplayRule[];
}

const isScalar = (v: unknown): v is Scalar =>
  v === null ||
  typeof v === 'string' ||
  typeof v === 'boolean' ||
  (typeof v === 'number' && Number.isFinite(v));

/**
 * Check a rule set (the API's PUT body, the settings page's draft). `knownFields`: the field ids
 * that may be named besides the catalogue's (the organisation's discovered readings); any
 * `reading.<key>` is accepted when it is omitted.
 */
export function validateRules(
  input: unknown,
  knownFields: ReadonlySet<string> | null = null
): ValidationResult {
  const errors: string[] = [];
  if (!Array.isArray(input)) return { ok: false, errors: ['rules must be a list'], rules: [] };
  if (input.length === 0) errors.push('Add at least one rule (or reset to the defaults)');
  if (input.length > LIMITS.rules) errors.push(`At most ${LIMITS.rules} rules`);
  const ids = new Set<string>();
  const out: DisplayRule[] = [];
  input.forEach((raw: unknown, i: number) => {
    const n = `Rule ${i + 1}`;
    if (!isObj(raw)) {
      errors.push(`${n}: not a rule`);
      return;
    }
    const r = raw as unknown as DisplayRule;
    const where = r.name ? `${n} (${String(r.name).slice(0, 40)})` : n;
    if (typeof r.id !== 'string' || !RULE_ID.test(r.id))
      errors.push(`${where}: the id must be lower-case letters, digits, - or _ (at most 64)`);
    else if (ids.has(r.id)) errors.push(`${where}: the id ${r.id} is used twice`);
    else ids.add(r.id);
    if (r.layer !== 'base' && r.layer !== 'overlay')
      errors.push(`${where}: choose a colour (base) or a mark (overlay)`);
    if (r.name !== undefined && (typeof r.name !== 'string' || r.name.length > LIMITS.name))
      errors.push(`${where}: the name is at most ${LIMITS.name} characters`);
    if (r.legend !== undefined && (typeof r.legend !== 'string' || r.legend.length > LIMITS.legend))
      errors.push(`${where}: the legend text is at most ${LIMITS.legend} characters`);
    if (r.enabled !== undefined && typeof r.enabled !== 'boolean')
      errors.push(`${where}: enabled is true or false`);
    if (
      r.priority !== undefined &&
      !(Number.isInteger(r.priority) && Math.abs(r.priority) <= LIMITS.priority)
    )
      errors.push(
        `${where}: the priority is a whole number from -${LIMITS.priority} to ${LIMITS.priority}`
      );
    if (!isObj(r.visual)) {
      errors.push(`${where}: say what to show`);
      return;
    }
    if (!isObj(r.when)) {
      errors.push(`${where}: give a condition`);
      return;
    }
    if (nestsDeeper(r.when, LIMITS.depth)) {
      errors.push(`${where}: conditions nest at most ${LIMITS.depth} deep`);
      return;
    }
    let rule: DisplayRule;
    try {
      rule = normaliseRule(r);
    } catch {
      errors.push(`${where}: the condition cannot be read`);
      return;
    }
    const v = rule.visual;
    if (
      v.label !== undefined &&
      (typeof v.label !== 'string' || v.label.trim() === '' || v.label.length > LIMITS.label)
    )
      errors.push(`${where}: the words are 1 to ${LIMITS.label} characters`);
    if (rule.layer === 'base') {
      if (!isDisplayToken(v.key)) errors.push(`${where}: choose a colour`);
    } else if (rule.layer === 'overlay') {
      if (!isOverlayMark(v.mark))
        errors.push(`${where}: choose a mark (badge, ring, pulse or dashed)`);
      else if (v.mark === 'badge' && !v.label) errors.push(`${where}: a badge needs its words`);
      else if ((v.mark === 'ring' || v.mark === 'pulse') && !isDisplayToken(v.key))
        errors.push(`${where}: choose the ring's colour`);
      if (v.key !== undefined && !isDisplayToken(v.key))
        errors.push(`${where}: that colour is not in the palette`);
    }
    let leaves = 0;
    const check = (c: unknown, depth: number): void => {
      if (!isObj(c)) {
        errors.push(`${where}: a condition is not readable`);
        return;
      }
      if (depth > LIMITS.depth) {
        errors.push(`${where}: conditions nest at most ${LIMITS.depth} deep`);
        return;
      }
      if ('always' in c) {
        if (c.always !== true) errors.push(`${where}: "always" is true`);
        return;
      }
      for (const k of ['all', 'any'] as const) {
        if (k in c) {
          const list = c[k];
          if (!Array.isArray(list) || list.length === 0)
            errors.push(`${where}: "${k}" needs at least one condition`);
          else list.forEach((x) => check(x, depth + 1));
          return;
        }
      }
      if ('not' in c) {
        check(c.not, depth + 1);
        return;
      }
      leaves++;
      if ('event' in c) {
        const type = c.event;
        if (typeof type !== 'string' || !EVENT_TYPE.test(type))
          errors.push(`${where}: an event is CATEGORY.TYPE or CATEGORY.*`);
        const w = Number(c.within_s);
        if (!(Number.isInteger(w) && w >= 60 && w <= MAX_EVENT_WINDOW_S))
          errors.push(`${where}: look back between 1 minute and 7 days`);
        return;
      }
      const path = c.path;
      const op = c.op as ConditionOp;
      if (typeof path !== 'string' || !path) {
        errors.push(`${where}: choose a field`);
        return;
      }
      const def = FIELD_BY_ID.get(path);
      // A reading the catalogue does not list: never an identity / position / time key, and on the
      // server only one the organisation's devices report (`knownFields`, the discovered set).
      const discovered =
        discoveredReadingKey(path) !== null && (!knownFields || knownFields.has(path));
      if (!def && !discovered) {
        errors.push(`${where}: ${path} is not a field of the catalogue`);
        return;
      }
      if (!(CONDITION_OPS as readonly string[]).includes(op)) {
        errors.push(`${where}: ${fieldLabel(path)}: choose how to compare`);
        return;
      }
      if (def && !OPS_BY_KIND[def.kind].includes(op)) {
        errors.push(`${where}: ${def.label} cannot be compared with "${OP_TEXT[op]}"`);
        return;
      }
      if (NO_VALUE_OPS.includes(op)) return;
      const value = c.value;
      const tooLong = (x: unknown) => typeof x === 'string' && x.length > LIMITS.value;
      if (LIST_OPS.includes(op)) {
        if (
          !Array.isArray(value) ||
          value.length === 0 ||
          value.length > LIMITS.listValues ||
          !value.every(isScalar)
        )
          errors.push(`${where}: ${fieldLabel(path)}: give 1 to ${LIMITS.listValues} values`);
        else if (value.some(tooLong))
          errors.push(
            `${where}: ${fieldLabel(path)}: a value is at most ${LIMITS.value} characters`
          );
        return;
      }
      if (tooLong(value)) {
        errors.push(`${where}: ${fieldLabel(path)}: a value is at most ${LIMITS.value} characters`);
        return;
      }
      if (
        !isScalar(value) ||
        value === null ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        errors.push(`${where}: ${fieldLabel(path)}: give a value`);
        return;
      }
      if (def?.kind === 'number' && asNumber(value) === null)
        errors.push(`${where}: ${def.label} is a number`);
      else if (def?.kind === 'boolean' && typeof value !== 'boolean')
        errors.push(`${where}: ${def.label} is yes or no`);
      else if (
        NUMERIC_OPS.includes(op) &&
        def?.kind !== 'number' &&
        asNumber(value) === null &&
        def
      )
        errors.push(`${where}: ${def.label} cannot be compared with "${OP_TEXT[op]}"`);
    };
    check(rule.when, 1);
    if (leaves > LIMITS.leaves)
      errors.push(`${where}: at most ${LIMITS.leaves} conditions in one rule`);
    out.push(rule);
  });
  if (!errors.length && storedJsonBytes(out) > LIMITS.bytes)
    errors.push('The rules are too large: use fewer rules, conditions or values');
  return errors.length ? { ok: false, errors, rules: [] } : { ok: true, errors: [], rules: out };
}

// ── Versions over time (Replay: the rule set in force at a replayed moment) ──────────────────

export interface RuleVersionSpan {
  version: number;
  /** In force from (ISO; null: since the beginning — the code defaults before the first save). */
  valid_from: string | null;
  /** Until (ISO, exclusive; null: still in force). */
  valid_to: string | null;
  rules: DisplayRule[];
}

/** The span in force at `tMs` (the earliest when `tMs` is before them all). */
export function versionAt<T extends Pick<RuleVersionSpan, 'valid_from' | 'valid_to'>>(
  spans: readonly T[],
  tMs: number
): T | null {
  let found: T | null = null;
  for (const s of spans) {
    const from = s.valid_from ? Date.parse(s.valid_from) : -Infinity;
    const to = s.valid_to ? Date.parse(s.valid_to) : Infinity;
    if (tMs >= from && tMs < to) return s;
    if (!found) found = s;
  }
  return found;
}
